import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Grid, Chip, Divider,
  CircularProgress, IconButton, Tooltip
} from '@mui/material';
import {
  CalendarMonth, School, MenuBook, People, MonetizationOn,
  LibraryBooks, CallSplit, Class as ClassIcon, Category,
  Home, Refresh, PlayArrow, Assessment, Scoreboard, EventNote,
  AccountBalance, Receipt, Grade, Tune, Insights, EmojiEvents, Analytics, Schedule, Groups, GridOn, Print,
  PersonPin
} from '@mui/icons-material';
import axios from 'axios';
import { fmtN } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;

const TILE_DEFS = [
  { label: 'Academic Years',  icon: CalendarMonth,   color: '#1565c0', path: '/academics/years',          api: '/api/academics/years',               desc: 'Set up academic years and terms with start/end dates'    },
  { label: 'Stages / Grades', icon: School,          color: '#2e7d32', path: '/academics/stages',         api: '/api/stages',                        desc: 'Define grade levels and their promotion sequence'        },
  { label: 'Learning Areas',  icon: MenuBook,        color: '#6a1b9a', path: '/academics/learning-areas', api: '/api/academics/learning-areas',      desc: 'Master list of subjects offered across all stages'       },
  { label: 'Stage Subjects',  icon: LibraryBooks,    color: '#0277bd', path: '/academics/stage-subjects', api: '/api/academics/stage-learning-areas',desc: 'Assign mandatory & optional subjects per stage per year'  },
  { label: 'Fee Templates',   icon: MonetizationOn,  color: '#00838f', path: '/academics/fee-templates',  api: '/api/academics/fee-templates',       desc: 'Fee structure per stage, term and student type'          },
  { label: 'Streams',         icon: CallSplit,       color: '#558b2f', path: '/academics/streams',         api: '/api/academics/streams',             desc: 'Define class streams (e.g. East, West, Blue)'            },
  { label: 'Classes',         icon: ClassIcon,       color: '#6d4c41', path: '/academics/classes',         api: '/api/academics/classes',             desc: 'Assign stages & streams into classroom units'            },
  { label: 'Charge Items',    icon: Category,        color: '#4527a0', path: '/academics/charge-items',    api: '/api/academics/charge-items',        desc: 'Fee charge types with payment sequence & GL link'        },
  { label: 'Learners',        icon: People,          color: '#e65100', path: '/learners',                  api: '/api/learners',                      desc: 'Admit and manage student records and bio-data'           },
  { label: 'Timetable Setup', icon: Schedule,        color: '#00695c', path: '/academics/timetable-setup', api: null, desc: 'Bell schedule — periods, breaks and per-band start offsets per term' },
  { label: 'Teacher Allocations', icon: Groups,      color: '#00897b', path: '/academics/teacher-allocations', api: '/api/timetable/allocations', desc: 'Assign teachers to classes & subjects — the demand behind the timetable' },
  { label: 'Subject Teachers',    icon: PersonPin,   color: '#00695c', path: '/academics/subject-teachers', api: '/api/academics/stage-subject-teachers', desc: 'Assign a teacher to a stage subject — they get its classes, students, marks & reports' },
  { label: 'Timetable Grid',   icon: GridOn,         color: '#00838f', path: '/academics/timetable-grid', api: null, desc: 'Place lessons into the week grid — validated per class and teacher' },
  { label: 'Timetable Views',  icon: Print,          color: '#455a64', path: '/academics/timetable-print', api: null, desc: 'Print per-class, per-teacher and master timetables' },
  { label: 'Periodic Activities', icon: PlayArrow,   color: '#f57c00', path: '/academics/periodic-activities', api: null, desc: 'Batch registration, invoicing, promotion and graduation' },
  { label: 'Assessment Setup',    icon: Assessment,  color: '#00838f', path: '/academics/assessment-setup',     api: null, desc: 'Define CATs, exams and projects per stage, subject and term' },
  { label: 'Score Entry',         icon: Scoreboard,  color: '#283593', path: '/academics/score-entry',          api: null, desc: 'Enter learner scores per assessment component (class list)' },
  { label: 'Assessment Reports',  icon: Assessment,  color: '#004d40', path: '/academics/assessment-report',    api: null, desc: 'View learner term reports and class summaries' },
  { label: 'Merit List',          icon: EmojiEvents, color: '#f9a825', path: '/academics/merit-list',           api: null, desc: 'Rank learners by mean score per class and stream' },
  { label: 'Pathway Insight',     icon: Insights,    color: '#00695c', path: '/academics/pathway-insight',      api: null, desc: 'CBC pathway lean per learner from performance (advisory)' },
  { label: 'LearnAlytics',        icon: Analytics,   color: '#00838f', path: '/academics/lernalytics',          api: null, desc: 'Learner performance over time — trends, projections & risks' },
  { label: 'School Events',       icon: EventNote,   color: '#ad1457', path: '/academics/events',               api: '/api/academics/events?all=true', desc: 'AGMs, visiting days, sports & ceremonies — shown to parents' },
  { label: 'Grading Scale (CBC)',       icon: Grade,          color: '#1565c0', path: '/setups/grading-scale',          api: '/api/academics/grading-scale',          desc: 'EE/ME/AE/BE percentage boundaries and colors' },
  { label: 'Learner Posting Groups',    icon: AccountBalance, color: '#0288d1', path: '/setups/learner-posting-groups', api: '/api/academics/learner-posting-groups', desc: 'Map learner categories to AR and advance GL accounts' },
  { label: 'VAT Posting Groups',        icon: Receipt,        color: '#f57c00', path: '/setups/vat-posting-groups',     api: '/api/academics/vat-posting-groups',     desc: 'VAT rates and output/input VAT GL accounts per charge' },
  { label: 'Posting Setup',             icon: Tune,           color: '#6a1b9a', path: '/setups/posting',                api: null,                                     desc: 'Map transaction types to GL accounts (AR, advance receipts)' },
];

const AcademicsDashboard = () => {
  const navigate = useNavigate();
  const [counts,  setCounts]  = useState({});
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled(
      TILE_DEFS.map(t => t.api ? axios.get(`${API}${t.api}`, { headers }) : Promise.resolve(null))
    );
    const newCounts = {};
    TILE_DEFS.forEach((t, i) => {
      if (results[i].status === 'fulfilled' && results[i].value) {
        const d = results[i].value.data;
        newCounts[t.label] = Array.isArray(d) ? d.length : (d?.total ?? d?.data?.length ?? 0);
      }
    });
    setCounts(newCounts);
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── KPI Cue ──────────────────────────────────────────────────────────────
  const Cue = ({ icon: Icon, label, value, color, path }) => (
    <Card elevation={2} sx={{
      borderRadius: 3, cursor: 'pointer', height: '100%',
      borderLeft: `4px solid ${color}`,
      transition: 'transform .15s, box-shadow .15s',
      '&:hover': { boxShadow: 5, transform: 'translateY(-2px)' }
    }} onClick={() => navigate(path)}>
      <CardContent sx={{ py: 1.8 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="h4" fontWeight={800} lineHeight={1} color={color}>
              {loading ? '…' : fmtN(value ?? 0)}
            </Typography>
            <Typography variant="body2" fontWeight={600} mt={0.5}>{label}</Typography>
          </Box>
          <Box sx={{
            width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${color}18`
          }}>
            <Icon sx={{ fontSize: 21, color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6f8', py: 3 }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 } }}>

        {/* Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 3,
          background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          color: '#fff', boxShadow: 3, flexWrap: 'wrap', gap: 2
        }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Academics Manager</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3 }}>
              Academic years · Stages · Classes · Fee Templates · Learners
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchAll} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Refresh />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Home">
              <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Activities cues */}
        <Box mb={1}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>Activities</Typography>
            <Divider sx={{ flex: 1, ml: 1.5 }} />
          </Box>
          <Grid container spacing={2}>
            {[
              { label: 'Learners',       icon: People,        color: '#e65100', key: 'Learners'        },
              { label: 'Classes',        icon: ClassIcon,     color: '#6d4c41', key: 'Classes'         },
              { label: 'Stages',         icon: School,        color: '#2e7d32', key: 'Stages / Grades' },
              { label: 'Fee Templates',  icon: MonetizationOn,color: '#00838f', key: 'Fee Templates'   },
              { label: 'Charge Items',   icon: Category,      color: '#4527a0', key: 'Charge Items'    },
            ].map(c => (
              <Grid item xs={12} sm={6} md={2.4} key={c.key}>
                <Cue icon={c.icon} label={c.label} color={c.color}
                  value={counts[c.key]}
                  path={TILE_DEFS.find(t => t.label === c.key)?.path || '/academics'} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box my={3}><Divider /></Box>

        {/* All tiles */}
        <Box>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>All Modules</Typography>
            <Divider sx={{ flex: 1, ml: 1.5 }} />
          </Box>
          <Grid container spacing={2.5}>
            {TILE_DEFS.map(({ label, icon: Icon, color, path, desc, api }) => (
              <Grid item xs={12} sm={6} md={3} key={label}>
                <Card sx={{
                  borderRadius: 3, cursor: 'pointer', height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  borderTop: !api ? `4px solid ${color}` : undefined,
                  '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }
                }} onClick={() => navigate(path)}>
                  <CardContent>
                    <Box display="flex" alignItems="flex-start" gap={2} mb={1}>
                      <Box sx={{
                        width: 46, height: 46, flexShrink: 0, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: `${color}1A`
                      }}>
                        <Icon sx={{ fontSize: 24, color }} />
                      </Box>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="subtitle2" fontWeight={600} fontSize={13} lineHeight={1.3}>{label}</Typography>
                        {!api
                          ? <Chip label="Open" size="small" sx={{ mt: 0.5, height: 20, fontSize: 12, bgcolor: `${color}22`, color }} />
                          : counts[label] !== undefined
                            ? <Chip label={fmtN(counts[label])} size="small"
                                sx={{ mt: 0.5, height: 20, fontSize: 12, bgcolor: `${color}22`, color }} />
                            : <Typography variant="caption" color="text.disabled">loading…</Typography>}
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" fontSize={12}>{desc}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

      </Box>
    </Box>
  );
};

export default AcademicsDashboard;
